import axios from "axios";
import dayjs from "dayjs";
import { message } from "antd";
import { CREATE_jwel, CREATE_jwel1 } from "../../Config/Config";

/* ════════════════════════════════════════════════════════════════
   PURCHASE INVOICE – DATA LAYER (no UI in this file)
   ════════════════════════════════════════════════════════════════ */

const API = `${CREATE_jwel}/api/ERP`;
// same host / route the Excel upload screen uses for counter_config + current_stock
const MASTER_API = `${CREATE_jwel1}/api/Master`;
const PURCH_API = `${CREATE_jwel}/api/PurchInvoice`;
const STOCK_API = `${CREATE_jwel}/api/CurrentStockApi`;

export const TABLES = {
  purchaseType: "PURCHASE_TYPE",
  area: "AREA_MAST",
  transport: "TRANSPORT_MAST",
  company: "COMPANY_MAST",
  group: "GROUP_MAST",
  item: "ITEM_MASTER",
  size: "SIZE_MAST",
  colour: "COLOUR_MAST",
  fitting: "FITTING_MAST",
  sleeves: "SLEVES_MAST",
  supplier: "SUPPLIER",
  counterConfig: "counter_config", // lowercase, as the working Excel screen calls it
  currentStock: "current_stock", // lowercase, as the working Excel screen calls it
  purchaseDataDet: "purchInvoice_DataDet", // design code max comes from here (rows of the purchase type)
  purchaseMaster: "PURCHINVOICE_MAST",
  transEntry: "TRANS_ENTRY_DATA",
  codeConfigure: "CODE_CONFIGURE", // digit → letter table for the dealer-price code
};

export const SAVE_EACH_NEW_ITEM = true;

const LOOKUPS = [
  "purchaseType",
  "area",
  "transport",
  "company",
  "group",
  "item",
  "size",
  "colour",
  "fitting",
  "sleeves",
  "codeConfigure",
];

export const FIELDS = {
  purchaseType: { name: ["PType"], code: ["PCode"], no: ["PNo"] },
  area: { name: ["Areaname"] },
  supplier: {
    name: ["Custname"],
    shortName: ["SName"],
    gstin: ["TIN"],
    pan: ["PAN"],
    station: ["Station"],
    area: ["area"],
    address: ["add1", "add2", "add3", "add4"],
  },
  transport: { name: ["TransName"] },
  company: { name: ["CompName"] },
  group: { name: ["GroupName"], hsn: ["HSNCODE", "HSN"] },
  item: { name: ["ProdName"], group: ["GroupName"] },
  size: { name: ["ProdSize"] },
  colour: { name: ["COLOUR"] },
  fitting: { name: ["FITTING"] },
  sleeves: { name: ["SLEVES"] },
  counterConfig: {
    terminalId: ["BARCODE_PREFIX"], // → DESIGN CODE
    // → ENTRY NO. Column name not confirmed: add the real one from the console log
    entryPrefix: ["ENTRY_PREFIX", "ENTRYPREFIX", "ENTRY_PRE", "ENTRYNO_PREFIX"],
  },
  codeConfigure: { no: ["NO"], chr: ["CHR"] },
  currentStock: { seq: ["MAXNUM"] },
  purchaseDataDet: { seq: ["MAXNUM"] },
  purchaseMaster: { entryNo: ["ENTRYNO"], pType: ["PType"] },
  transEntry: {
    entryNo: ["ENTRYNO"],
    entryDate: ["ENTRYDATE"],
    billNo: ["BILLNO"],
    billDate: ["BILLDATE"],
    party: ["PARTYNAME"],
    area: ["AREA"],
    transType: ["TRANS_TYPE"],
    particulars: ["PARTICULARS"],
    qty: ["JAMAQTY"],
    amount: ["JAMA"],
  },
};

/* ── helpers ─────────────────────────────────────────────────── */

export const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
export const money = (v) => num(v).toFixed(2);
const r2 = (v) => Math.round(num(v) * 100) / 100;
const esc = (v) => String(v ?? "").replace(/'/g, "''");

export const clean = (v) => {
  const s = String(v ?? "").trim();
  return s === "-" ? "" : s;
};

export const pick = (row, names, fallback = "") => {
  if (!row) return fallback;
  const keys = Object.keys(row);
  for (const n of names) {
    const k = keys.find((x) => x.toLowerCase() === n.toLowerCase());
    if (k !== undefined && row[k] !== null && row[k] !== undefined) {
      return row[k];
    }
  }
  return fallback;
};

const numStr = (v) => {
  const n = num(v);
  return n ? String(n) : "";
};

export const toOptions = (rows = [], names) =>
  [...new Set(rows.map((r) => clean(pick(r, names, ""))).filter(Boolean))].map(
    (v) => ({ label: v, value: v }),
  );

export const apiError = (err, fallback = "Request failed") => {
  const d = err?.response?.data;
  if (typeof d === "string" && d.trim()) return d;
  return d?.message || d?.title || err?.message || fallback;
};

/* ── API calls: masters ──────────────────────────────────────── */

export const fetchTable = async (tableName, tenantName) => {
  const res = await axios.get(`${API}/GetDataFromGivenTableName`, {
    params: { tableName },
    headers: { tenantName },
  });
  return Array.isArray(res.data) ? res.data : [];
};

// counter_config + current_stock: identical call to ExcelUploadPurchase (known to work)
const fetchMasterTable = async (tableName, tenantName) => {
  const res = await axios.get(`${MASTER_API}/GetDataFromGivenTableName`, {
    params: { tableName },
    headers: { tenantName },
  });
  return Array.isArray(res.data) ? res.data : [];
};

const fetchTableWhere = async (tableName, where, tenantName) => {
  const res = await axios.get(`${API}/GetDataFromGivenTableNameWithWhere`, {
    params: { tableName, where },
    headers: { tenantName },
  });
  return Array.isArray(res.data) ? res.data : [];
};

// loads EVERY master at once (kept for other screens – Purchase Invoice no longer uses it)
export const loadAllMasters = async (tenantName) => {
  const results = await Promise.allSettled(
    LOOKUPS.map((k) => fetchTable(TABLES[k], tenantName)),
  );
  const out = {};
  results.forEach((r, i) => {
    out[LOOKUPS[i]] = r.status === "fulfilled" ? r.value : [];
    if (r.status === "rejected") {
      console.error(`Failed to load ${TABLES[LOOKUPS[i]]}`, r.reason);
    }
  });
  return out;
};

// load ONE master table (used lazily by the screen, only when its field is needed)
export const loadMaster = (key, tenantName) =>
  fetchTable(TABLES[key], tenantName);

/* ════════════════════════════════════════════════════════════════
   AUTOMATIC GST % (from the PURCHASE_TYPE row)
   ────────────────────────────────────────────────────────────────
   1) ALLOWGSTVALUE && EFFECTTOPURCHASES  → slab by Gross DP
        Gross DP <  BELLOWVALUE → BELLOWPER
        Gross DP >  ABOVEVALUE  → ABOVEPER
        anything else           → ABOVEPER
      (if the slab is switched on but every slab value is 0 and a
       DEFAULTGSTPER exists, the default is used instead)
   2) DEFAULTGSTVALE flag on, OR DEFAULTGSTPER > 0 → DEFAULTGSTPER
   3) nothing applies                              → null (GST stays manual)

   Examples (READYMADES: BELLOWVALUE 2500 / 5%, ABOVEVALUE 2499 / 18%)
        Gross DP 1000 → 5      Gross DP 2499 → 5      Gross DP 2500 → 18      Gross DP 2700 → 18
   TEXTILES (below value 0) → every price uses ABOVEPER (5)
   HANDLOOMS (default GST)  → always 10

   TOLERANT KEY READING: column names are matched ignoring case, underscores and
   spaces, and the common spellings (BELLOW / BELOW, VALE / VALUE) are all tried,
   so a tenant that names a column slightly differently still works.
   If nothing applies, one console.warn per purchase type lists the columns the
   row really has, so the cause is visible in the browser console (F12).
   ════════════════════════════════════════════════════════════════ */

// GST-START
const normKey = (k) =>
  String(k)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

// read a column of the purchase type row, trying several spellings
const typeVal = (row, names, fallback = undefined) => {
  const want = names.map(normKey);
  const k = Object.keys(row).find((x) => want.includes(normKey(x)));
  return k !== undefined && row[k] !== null && row[k] !== undefined
    ? row[k]
    : fallback;
};

const isOn = (v) =>
  v === true ||
  v === 1 ||
  ["true", "1", "yes", "y"].includes(
    String(v ?? "")
      .trim()
      .toLowerCase(),
  );

// DEFAULTGSTPER: exact spellings first, then any column that looks like "default … gst … per"
const readDefaultPer = (row) => {
  const exact = typeVal(row, ["DEFAULTGSTPER", "DEFAULTGSTPERCENT"]);
  if (exact !== undefined) return num(exact);
  const k = Object.keys(row).find((x) => {
    const n = normKey(x);
    return n.includes("DEFAULT") && n.includes("GST") && n.includes("PER");
  });
  return k !== undefined ? num(row[k]) : 0;
};

const warnedTypes = new Set();

export const resolveGstPer = (typeRow, grossDp) => {
  if (!typeRow) return null;

  const allowSlab = isOn(typeVal(typeRow, ["ALLOWGSTVALUE"], false));
  const effectPurch = isOn(typeVal(typeRow, ["EFFECTTOPURCHASES"], false));
  const defaultFlag = isOn(
    typeVal(typeRow, ["DEFAULTGSTVALE", "DEFAULTGSTVALUE"], false),
  );
  const defaultPer = readDefaultPer(typeRow);

  const belowVal = num(typeVal(typeRow, ["BELLOWVALUE", "BELOWVALUE"], 0));
  const belowPer = num(typeVal(typeRow, ["BELLOWPER", "BELOWPER"], 0));
  const aboveVal = num(typeVal(typeRow, ["ABOVEVALUE"], 0));
  const abovePer = num(typeVal(typeRow, ["ABOVEPER"], 0));

  const slabEmpty = !belowVal && !belowPer && !aboveVal && !abovePer;

  // 1) slab by Gross DP
  if (allowSlab && effectPurch && !(slabEmpty && defaultPer > 0)) {
    const gp = num(grossDp);
    if (gp <= 0) return null; // wait until Gross DP is typed
    if (belowVal > 0 && gp < belowVal) return belowPer; // below the BELOW value
    if (gp > aboveVal) return abovePer; // above the ABOVE value
    return abovePer; // exactly on a limit / between the two values
  }

  // 2) default GST of the purchase type (flag on, or a default % is filled)
  if (defaultFlag || defaultPer > 0) return defaultPer;

  // 3) nothing applies → manual. Tell the console once per purchase type.
  const label = String(typeVal(typeRow, ["PTYPE"], "?"));
  if (!warnedTypes.has(label)) {
    warnedTypes.add(label);
    console.warn(
      `GST: no rule found for purchase type "${label}". Columns on the row:`,
      Object.keys(typeRow),
      typeRow,
    );
    // also shown ON SCREEN (30 s) so the real column names / values can be read without the console
    const cols = Object.entries(typeRow)
      .map(([k, v]) => `${k}=${v}`)
      .join(", ");
    setTimeout(
      () =>
        message.warning({
          content: `GST not found for "${label}" → ${cols}`,
          duration: 30,
        }),
      0,
    );
  }
  return null;
};
// GST-END

export const fetchSuppliersByArea = async (tenantName, area) => {
  if (!area) return [];
  return fetchTableWhere(TABLES.supplier, `area='${esc(area)}'`, tenantName);
};

/* ════════════════════════════════════════════════════════════════
   SHARED PIECES
   ════════════════════════════════════════════════════════════════ */

export const getFyearCode = (d = new Date()) =>
  String((d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1) % 100);

export const getFyearFull = (d = new Date()) => {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${String(y).slice(-2)}${String(y + 1).slice(-2)}`;
};

/* counter_config → BARCODE_PREFIX (design code) + ENTRY prefix (entry no)
   Small table – safe to read when the page opens. */
export const fetchCounterConfig = async (tenantName) => {
  const cfg = await fetchMasterTable(TABLES.counterConfig, tenantName);

  // ► send me this log if the entry prefix still comes back empty
  console.log("counter_config row:", cfg[0]);

  const p = Number(pick(cfg[0], FIELDS.counterConfig.terminalId, NaN));
  const entryPrefix = String(
    pick(cfg[0], FIELDS.counterConfig.entryPrefix, ""),
  ).trim();

  return {
    prefix: Number.isFinite(p) ? String(Math.trunc(p)) : "", // barcode prefix → DESIGN CODE
    entryPrefix, // entry prefix → ENTRY NO
  };
};

/* purchInvoice_DataDet rows of THIS PURCHASE TYPE → highest MAXNUM (design code).
     where PType = '<purchase type name>'   (e.g. READYMADES)
   Highest MAXNUM + 1 = the next design code number.
   No rows yet for that type → 0, so the first item gets MAXNUM 1.
   Called when the entry no is created (purchase type picked / Reset) and when a
   saved invoice is opened (with that invoice's purchase type). */
export const fetchMaxSeq = async (tenantName, purchaseType) => {
  const type = String(purchaseType ?? "").trim();
  if (!type) return 0;
  const rows = await fetchTableWhere(
    TABLES.purchaseDataDet,
    `PType='${esc(type)}'`,
    tenantName,
  );
  return rows.reduce((m, r) => {
    const n = parseInt(pick(r, FIELDS.purchaseDataDet.seq, ""), 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
};

/* ════════════════════════════════════════════════════════════════
   1) ENTRY NO  (your note)
      ① purchase type → entry code  (PURCHASE_TYPE.PNo)
      ② counter_config → Entry prefix
      ③ financial year
      ④ PURCHINVOICE_MAST rows of that purchase type, length + 1

      entryNo = PNo + EntryPrefix + FY + (length + 1)
   If the order must change, change ONLY buildEntryNoPrefix.
   ════════════════════════════════════════════════════════════════ */
export const buildEntryNoPrefix = ({
  purchaseTypeNo,
  entryPrefix,
  fyearCode,
}) => `${purchaseTypeNo}${entryPrefix}${fyearCode}`;

export const fetchNextEntryNo = async (
  tenantName,
  { purchaseType, purchaseTypeNo, entryPrefix, fyearCode },
) => {
  const rows = await fetchTableWhere(
    TABLES.purchaseMaster,
    `PType='${esc(purchaseType)}'`,
    tenantName,
  );
  const prefix = buildEntryNoPrefix({ purchaseTypeNo, entryPrefix, fyearCode });
  return `${prefix}${rows.length + 1}`;
};

/* ════════════════════════════════════════════════════════════════
   2) DESIGN CODE  –  ONE per item row
      designCode = <PCode><FY><Terminal> + (MAXNUM + 1 + n)
      PCode  = PURCHASE_TYPE.PCode
      MAXNUM = highest MAXNUM of purchInvoice_DataDet where PType = purchase type
               (see fetchMaxSeq)
      n      = how many items this invoice has already used (never reused),
               so a saved row keeps its code even if another row is deleted.
   ════════════════════════════════════════════════════════════════ */
export const buildDesignCode = (pcode, base, n) =>
  pcode && base.prefix !== null && base.maxSeq !== null
    ? `${pcode}${getFyearCode()}${base.prefix}${base.maxSeq + 1 + n}`
    : "";

// values frozen on a row the moment it is added
export const newRowKeys = (pcode, base, n) => ({
  designCode: buildDesignCode(pcode, base, n),
  maxnum: (base.maxSeq || 0) + 1 + n,
  terminalid: Number(base.prefix) || 0,
});

/* ════════════════════════════════════════════════════════════════
   SALE ROUND-OFF  (retail-store price endings)
   ────────────────────────────────────────────────────────────────
   Applied ONLY to the Cust Price that is calculated from Sale %.

     GENERAL → nearest whole rupee: .50 and above goes UP, below .50 stays
               213.89 → 214     614.23 → 614     213.50 → 214
     "0"     → nearest price ending in 0   612 → 610   667 → 670   615 → 620
     "5"     → nearest price ending in 5   612 → 615   618 → 615   609 → 605
     "9"     → nearest price ending in 9   814 → 819   886 → 889   811 → 809

   For 0 / 5 / 9 an exact tie goes UP (like normal rounding); paise are rounded
   too (681.63 with "0" → 680).
   To add another ending (e.g. "4"), add it to ROUND_ENDINGS only.
   ════════════════════════════════════════════════════════════════ */
const ROUND_ENDINGS = { 0: 0, 5: 5, 9: 9 };

export const applyRoundOff = (value, mode) => {
  const v = num(value);
  if (v <= 0) return v;

  const key = String(mode ?? "").trim();
  if (!(key in ROUND_ENDINGS)) return Math.round(v); // GENERAL → whole rupee, no paise

  const d = ROUND_ENDINGS[key];
  let out = Math.round((v - d) / 10) * 10 + d; // nearest number that ends in d
  if (out < 0) out += 10; // tiny prices (e.g. 3 with "9") must not go negative
  return out;
};

/* ════════════════════════════════════════════════════════════════
   DEALER-PRICE CODE  (shown in Purchase Values, saved as "code")
   ────────────────────────────────────────────────────────────────
   CODE_CONFIGURE rows look like  { NO: 0, CHR: "M" }, { NO: 1, CHR: "L" } ...
   Every digit of the Dir Price (dealer price) is swapped for its letter.
   The Dir Price is first rounded to a whole rupee (same rule as Cust Price),
   so 512.50 → 513 → digits 5,1,3 → "SLR" with the table above.
   Returns "" when the price is 0 or the table has no letter for a digit.
   If the decimals must be coded too, change ONLY buildPriceCode.
   ════════════════════════════════════════════════════════════════ */
export const buildCodeMap = (rows = []) =>
  rows.reduce((map, r) => {
    const no = parseInt(pick(r, FIELDS.codeConfigure.no, ""), 10);
    const chr = clean(pick(r, FIELDS.codeConfigure.chr, ""));
    if (Number.isFinite(no) && chr) map[String(no)] = chr;
    return map;
  }, {});

export const buildPriceCode = (dirPrice, codeMap) => {
  const whole = Math.round(num(dirPrice));
  if (whole <= 0 || !codeMap) return "";
  const letters = String(whole)
    .split("")
    .map((d) => codeMap[d]);
  return letters.every(Boolean) ? letters.join("") : "";
};

/* ── Sale % <-> Cust Price (same idea as wholesale) ───────────────
     saleMode "per"   : Cust Price = Dir Price × (1 + Sale% / 100), then ROUND-OFF
                        Sale % is then re-worked from the rounded price so the
                        saved Sale % always matches the saved Cust Price.
     saleMode "price" : Sale %     = (Cust Price / Dir Price − 1) × 100
                        a price typed by the user (or loaded from a saved
                        invoice) is final – it is NEVER rounded.
   saleMode = whichever of the two the user typed last.
   ────────────────────────────────────────────────────────────── */
export const resolveSale = (dirPrice, i, roundoff = "GENERAL") => {
  if (i.saleMode === "price") {
    const cust = num(i.custPrice);
    const per = dirPrice > 0 && cust > 0 ? (cust / dirPrice - 1) * 100 : 0;
    return { salePer: r2(per), custPrice: cust };
  }
  const typedPer = num(i.salePer);
  const raw = dirPrice > 0 ? dirPrice * (1 + typedPer / 100) : 0;
  const cust = r2(applyRoundOff(raw, roundoff));

  // General: keep exactly the Sale % that was typed. 0 / 5 / 9: use the real % of the rounded price.
  const rounded = String(roundoff) in ROUND_ENDINGS;
  const per =
    rounded && dirPrice > 0 && cust > 0
      ? r2((cust / dirPrice - 1) * 100)
      : typedPer;
  return { salePer: per, custPrice: cust };
};

/* ── Calculations (PROVISIONAL purchase part – replace once confirmed) ──
     base     = Gross DP × Qty
     + plus%  → − purchase discount% → + GST%
   `roundoff` = footer.roundoff ("GENERAL" | "0" | "5" | "9")
   i.gstPer must already be the GST actually used (auto or typed).
   ────────────────────────────────────────────────────────────── */
export const calcPurchaseRow = (i, roundoff = "GENERAL") => {
  const qty = num(i.qty);
  const base = num(i.grossDp) * qty;
  const plusValue = (base * num(i.plusPer)) / 100;
  const afterPlus = base + plusValue;
  const discValue = (afterPlus * num(i.purDisPer)) / 100;
  const taxable = afterPlus - discValue;
  const gstValue = (taxable * num(i.gstPer)) / 100;
  const grossAmt = taxable + gstValue;
  const dirPrice = qty > 0 ? grossAmt / qty : 0;

  const wPrice = dirPrice * (1 + num(i.wPer) / 100);

  const sale = resolveSale(dirPrice, i, roundoff);
  const disRs = (sale.custPrice * num(i.disPer)) / 100;
  const offerPrice = sale.custPrice - disRs;

  return {
    amount: base,
    plusValue,
    discValue,
    gstValue,
    grossAmt,
    dirPrice,
    wPrice,
    salePer: sale.salePer,
    custPrice: sale.custPrice,
    disRs,
    offerPrice,
  };
};

export const calcTotals = (rows, footer) => {
  const sum = (k) => rows.reduce((a, r) => a + num(r[k]), 0);
  const totalAmount = sum("amount");
  const plusValue = sum("plusValue");
  const discValue = sum("discValue");
  const gstValue = sum("gstValue");
  const grossAmount = sum("grossAmt");
  const tradeDisAmt = (grossAmount * num(footer.tradeDisPer)) / 100;
  const netAmount =
    grossAmount -
    tradeDisAmt +
    num(footer.otherAdditions) +
    num(footer.freight) +
    num(footer.otherChrgs) +
    num(footer.adjustment);
  return {
    totalQty: sum("qty"),
    totalAmount,
    plusValue,
    discValue,
    gstValue,
    grossAmount,
    tradeDisAmt,
    netAmount,
  };
};

/* ════════════════════════════════════════════════════════════════
   PURCHASE INVOICE – SAVE / UPDATE / DELETE
   ════════════════════════════════════════════════════════════════ */

const fmtDT = (d) => dayjs(d || new Date()).format("YYYY-MM-DDTHH:mm:ss");

// the key the Delete API (and the header lock) is based on
export const supplierKey = (header, supplier) => ({
  invNo: String(header.invoiceNo || "").trim(),
  supplierName: clean(pick(supplier, FIELDS.supplier.name)),
  city: clean(pick(supplier, FIELDS.supplier.area)) || header.city || "",
});

/* One payload for Save AND Update:
   { purchInvoiceMast:[1], purchInvoiceData:[rows], purchInvoiceDataDet:[rows] }

   Field mapping (screen → payload):
     Brand Name → company          Item Category → groupName
     Item Name  → itemName         Size → prodSize        Color → colour
     Fashion    → fitting (+ code) Offer (SLEVES) → sleves
     Brand Code (barcode/EAN) → itemcode ("0" when empty)
     Qty → qty, eqty, iteM_QTY, totpcs, actqty
     Cust Price (MRP) → custPrice AND offer
     Sale % → custPer      Dis % → disper      Rs. → disAmt
     Gross DP → grosDPrice  Dir Price → dlrPrice
     + % → dlrPLUSPER/AMT   PurDis % → dlrDISPER/AMT
     Design Code / maxnum / terminalid come frozen from each row.

   `entryDate` (optional) = the ORIGINAL entry date when an existing
   invoice is being updated, so it is not overwritten with "now".     */
export const buildPurchasePayload = ({
  header,
  supplier,
  entryNo,
  entryDate: savedEntryDate,
  rows,
  totals,
  footer,
}) => {
  const key = supplierKey(header, supplier);
  const station =
    clean(pick(supplier, FIELDS.supplier.station)).toUpperCase() || "LOCAL";
  const isOut = station === "OUTSTATION";
  const entryNoNum = Number(entryNo) || 0;
  const invDate = fmtDT(header.invoiceDate);
  const srecDate = fmtDT(header.stockRecDate);
  const entryDate = fmtDT(savedEntryDate || new Date());
  const fyear = getFyearFull();
  const ptype = header.purchaseType || "";
  const str = (v) => String(v ?? "").trim();

  const lines = rows.map((r, i) => {
    const sno = i + 1;
    const qty = num(r.qty);
    const gstPer = num(r.gstPer);
    const gstAmt = r2(r.gstValue);
    const mrp = r2(r.custPrice);
    const ecode = `${entryNoNum} / ${sno}`;
    return {
      invno: key.invNo,
      invdate: invDate,
      area: key.city,
      custName: key.supplierName,
      sno,
      company: str(r.brandName),
      groupName: str(r.itemCategory),
      itemName: str(r.itemName),
      prodSize: str(r.size),
      itemcode: str(r.brandCode) || "0",
      designCode: r.designCode || "",
      code: str(r.priceCode), // dealer-price code (CODE_CONFIGURE). Fashion still goes in "fitting"
      qty,
      dlrPrice: r2(r.dirPrice),
      custPer: num(r.salePer),
      custPrice: mrp,
      disper: num(r.disPer),
      disAmt: r2(r.disRs),
      amount: r2(r.amount),
      dlrDISPER: num(r.purDisPer),
      dlrDISAMT: r2(r.discValue),
      token: num(r.token),
      vatamt: gstAmt,
      gamount: r2(r.grossAmt),
      netamt: r2(r.grossAmt),
      cancelled: "NO",
      sStatus: false,
      hid: 0,
      bno: 0,
      colour: str(r.colour),
      fitting: str(r.fitting),
      sleves: str(r.offer) || "-",
      custVPER: 0,
      ecode,
      eqty: qty,
      entryno: entryNoNum,
      entrydate: entryDate,
      acity: key.city,
      agentname: "",
      offer: mrp,
      grosDPrice: num(r.grossDp),
      taans: false,
      fsize: "",
      tsize: "",
      differ: "",
      iteM_QTY: qty,
      dlrPLUSPER: num(r.plusPer),
      dlrPLUSAMT: r2(r.plusValue),
      pricetype: "",
      cpless: 0,
      cpplus: 0,
      branchname: "",
      splititem: false,
      ctoken: 0,
      pvatper: 0,
      pvatamt: 0,
      cno: 0,
      ptype,
      icodetype: "0",
      gst: gstPer,
      cgstrate: isOut ? 0 : gstPer / 2,
      cgstamt: isOut ? 0 : r2(gstAmt / 2),
      sgstrate: isOut ? 0 : gstPer / 2,
      sgstamt: isOut ? 0 : r2(gstAmt / 2),
      igstrate: isOut ? gstPer : 0,
      igstamt: isOut ? gstAmt : 0,
      hsncode: str(r.hsn),
      gstamt: gstAmt,
      resellerprice: 0,
      semiwsalerprice: 0,
      floor: "",
      free: num(r.free),
      totpcs: qty,
      dlrdamt: 0,
      boxes: 0,
      loosepcs: 0,
      packpcs: 0,
      caseratedp: 0,
      caseratecp: 0,
      caseraterp: 0,
      actqty: qty,
      dlrdispeR2: 0,
      dlrdisamT2: 0,
      dlrdispeR3: 0,
      dlrdisamT3: 0,
      dplusper: 0,
      dplusamt: 0,
      length: 0,
      lqty: 0,
      wper: num(r.wPer),
      wprice: r2(r.wPrice),
      oqtY2: 0,
      oqtY3: 0,
      oqtY4: 0,
      pntmodel: "",
      smodel: "",
      fyear,
      clouD_UPLOAD: true,
    };
  });

  const detail = lines.map((l, i) => ({
    ...l,
    srecdate: srecDate,
    terminalid: num(rows[i].terminalid),
    maxnum: num(rows[i].maxnum),
  }));

  const gst = totals.gstValue;
  const avgGstPer = rows.length
    ? rows.reduce((a, r) => a + num(r.gstPer), 0) / rows.length
    : 0;
  const net = r2(totals.netAmount);

  const master = {
    invno: key.invNo,
    invdate: invDate,
    pType: ptype,
    area: key.city,
    partyname: key.supplierName,
    grossamt: r2(totals.totalAmount),
    disrate: num(footer.tradeDisPer),
    tradedis: r2(totals.tradeDisAmt),
    adjPer: 0,
    adjAmt: num(footer.adjustment),
    vatrate: 0,
    vatamt: r2(gst),
    evat: 0,
    evatamt: 0,
    netamt: net,
    paidAmt: 0,
    balanceAmt: net,
    cancelled: "NO",
    narration: str(footer.narration),
    lrno: str(header.lrNo),
    lrdate: fmtDT(header.lrDate),
    transport: str(header.transport),
    ddays: num(header.dueDays),
    weight: "",
    srecdate: srecDate,
    fcharges: num(footer.freight),
    hid: 0,
    mode: "",
    accno: "",
    cardno: "",
    amount: 0,
    ddchr: 0,
    cstatus: true,
    custPer: 0,
    charges: 0,
    pstatus: "",
    exduty: true,
    totalamt: r2(totals.totalAmount),
    discount: r2(totals.discValue),
    cvper: 0,
    cvamt: 0,
    ocharges: num(footer.otherChrgs),
    station,
    oaddition: num(footer.otherAdditions),
    entryno: entryNoNum,
    entrydate: entryDate,
    acity: key.city,
    agentname: "",
    taans: true,
    branchname: "",
    dlrplusamt: r2(totals.plusValue),
    crNO: "",
    crAMT: 0,
    crStatus: true,
    imgpatH1: "",
    imgpatH2: "",
    imgpatH3: "",
    imgpatH4: "",
    imgpatH5: "",
    invpayableamt: net,
    invpaidamt: 0,
    invrtnamt: 0,
    invbalamt: net,
    invstatus: true,
    totcgstrate: isOut ? 0 : avgGstPer / 2,
    totcgstamt: isOut ? 0 : r2(gst / 2),
    totsgstrate: isOut ? 0 : avgGstPer / 2,
    totsgstamt: isOut ? 0 : r2(gst / 2),
    totigstrate: isOut ? avgGstPer : 0,
    totigstamt: isOut ? r2(gst) : 0,
    totgstamt: r2(gst),
    floor: "",
    smodel: "",
    totqty: totals.totalQty,
    fyear,
    clouD_UPLOAD: true,
  };

  return {
    purchInvoiceMast: [master],
    purchInvoiceData: lines,
    purchInvoiceDataDet: detail,
  };
};

/* ── API calls: invoice ──────────────────────────────────────────
   Verbs for Update / Delete were not specified – POST like Save.
   If swagger says PUT / DELETE, change only these three functions
   (for DELETE with a body: axios.delete(url, { data: key, headers })). */
export const savePurchInvoice = (tenantName, payload) =>
  axios.post(`${PURCH_API}/Save`, payload, { headers: { tenantName } });

export const updatePurchInvoice = (tenantName, payload) =>
  axios.post(`${PURCH_API}/Update`, payload, { headers: { tenantName } });

// key = { invNo, supplierName, city }  → removes the WHOLE invoice
export const deletePurchInvoice = (tenantName, key) =>
  axios.post(`${PURCH_API}/Delete`, key, { headers: { tenantName } });

/* ════════════════════════════════════════════════════════════════
   CURRENT STOCK API  (TRANS_ENTRY_DATA – one row per invoice)
   ════════════════════════════════════════════════════════════════
   Called ONLY from the final SAVE / UPDATE button:
     new invoice      → CurrentStockApi/Save
     loaded invoice   → CurrentStockApi/Update
   Add Item never touches it.

   Row layout (matches the TRANS_ENTRY_DATA rows you sent):
     particulars / tranS_TYPE = purchase type  (READYMADES / TEXTILES)
     jamaqty = total qty       jama = NET amount (credit to the supplier)
   ════════════════════════════════════════════════════════════════ */
export const buildCurrentStockPayload = ({
  header,
  supplier,
  entryNo,
  entryDate: savedEntryDate,
  totals,
  footer,
}) => {
  const key = supplierKey(header, supplier);
  const entryDate = fmtDT(savedEntryDate || new Date());
  const ptype = header.purchaseType || "";
  const str = (v) => String(v ?? "").trim();

  return [
    {
      area: key.city,
      partyname: key.supplierName,
      billno: key.invNo,
      billdate: fmtDT(header.invoiceDate),
      entryno: Number(entryNo) || 0,
      entrydate: entryDate,
      particulars: ptype,
      jamaqty: num(totals.totalQty),
      namaqty: 0,
      jama: r2(totals.netAmount),
      nama: 0,
      narration: str(footer.narration),
      hid: 0,
      agentcity: "",
      agentname: "",
      tranS_TYPE: ptype,
      cusT_TYPE: "",
      sno: 0,
      pstatus: "",
      salestype: "",
      chequeno: "",
      chqdate: entryDate,
      chqamt: 0,
      chqstatus: true,
      accno: "",
      chqcancell: "",
      description: "",
      lname: key.supplierName,
      groupname: "",
      cjama: 0,
      cnama: 0,
      pjama: 0,
      pnama: 0,
      stype: "",
      sdate: entryDate,
      gjama: 0,
      gnama: 0,
      touch: 0,
      vno: "",
      vtype: "",
      cuT_METAL: 0,
      cuT_TOUCH: 0,
      cuT_FINE: 0,
      rate: 0,
      cuT_AMOUNT: 0,
      vaT_PER: 0,
      vaT_AMOUNT: 0,
      cuT_NETAMOUNT: 0,
      jstonewt: 0,
      jnwt: 0,
      jamount: 0,
      nstonewt: 0,
      nnwt: 0,
      namount: 0,
      clouD_UPLOAD: true,
    },
  ];
};

// payload = array returned by buildCurrentStockPayload
export const saveCurrentStock = (tenantName, payload) =>
  axios.post(`${STOCK_API}/Save`, payload, { headers: { tenantName } });

export const updateCurrentStock = (tenantName, payload) =>
  axios.post(`${STOCK_API}/Update`, payload, { headers: { tenantName } });

// key = { invNo, supplierName, city }  (same key as the purchase delete)
export const deleteCurrentStock = (tenantName, key) =>
  axios.post(`${STOCK_API}/Delete`, key, { headers: { tenantName } });

/* ════════════════════════════════════════════════════════════════
   SAVED INVOICES  –  LIST (TRANS_ENTRY_DATA) + LOAD ONE
   ════════════════════════════════════════════════════════════════ */

/* List for the "Saved invoices" dialog.
   TRANS_ENTRY_DATA also holds other vouchers (sales, receipts…), so only the
   rows whose TRANS_TYPE / PARTICULARS is one of the PURCHASE_TYPE names are kept.
   One entry no = one row (the one carrying the amount wins). Newest first. */
export const fetchPurchaseList = async (tenantName, typeNames = []) => {
  const rows = await fetchTable(TABLES.transEntry, tenantName);
  const allowed = new Set(
    typeNames.map((t) => String(t).trim().toUpperCase()).filter(Boolean),
  );
  const F = FIELDS.transEntry;
  const byEntry = new Map();

  rows.forEach((r) => {
    const trans = clean(pick(r, F.transType)).toUpperCase();
    const part = clean(pick(r, F.particulars)).toUpperCase();
    // if the purchase types could not be loaded, do not hide anything
    if (allowed.size && !allowed.has(trans) && !allowed.has(part)) return;

    const entryNo = Number(pick(r, F.entryNo, NaN));
    if (!Number.isFinite(entryNo) || entryNo <= 0) return;

    const item = {
      entryNo,
      entryDate: pick(r, F.entryDate, ""),
      billNo: clean(pick(r, F.billNo)),
      billDate: pick(r, F.billDate, ""),
      party: clean(pick(r, F.party)),
      area: clean(pick(r, F.area)),
      type: clean(pick(r, F.transType)) || clean(pick(r, F.particulars)),
      qty: num(pick(r, F.qty, 0)),
      amount: num(pick(r, F.amount, 0)),
    };
    const old = byEntry.get(entryNo);
    if (!old || item.amount > old.amount) byEntry.set(entryNo, item);
  });

  return [...byEntry.values()].sort(
    (a, b) =>
      dayjs(b.entryDate).valueOf() - dayjs(a.entryDate).valueOf() ||
      b.entryNo - a.entryNo,
  );
};

/* One CURRENT_STOCK row → one row of the Added-items table.
   Column names (confirmed from your CURRENT_STOCK response):
     CompName → Brand        GroupName → Item Category   ProdName → Item Name
     Prodsize → Size         COLOUR → Color              FITTING → Fashion
     SLEVES → Offer          ItemCode → Brand Code       DesignCode → Design Code
     Qty → Qty               GrosDPrice → Gross DP       GST → GST %
     CPrice → Cust Price     Disper → Dis %              HSNCODE → HSN
     WPER → Wholesale %      MAXNUM / TERMINALID → frozen design keys
   The calculated fields (amount, gross, dir price …) are recomputed with the same
   calcPurchaseRow the screen uses, so edit + totals behave exactly like new rows.
   saleMode "price": the saved Cust Price (MRP) stays exactly as saved – it is
   never rounded again (resolveSale skips round-off in "price" mode).
   gstMode "manual": the saved GST is kept until Gross DP is edited. */
const stockToRow = (s, base, codeMap) => {
  const item = {
    brandCode: String(pick(s, ["ItemCode"], "0")).trim() || "0",
    brandName: clean(pick(s, ["CompName"])),
    itemCategory: clean(pick(s, ["GroupName"])),
    itemName: clean(pick(s, ["ProdName"])),
    hsn: clean(pick(s, ["HSNCODE"])),
    size: clean(pick(s, ["Prodsize"])),
    colour: clean(pick(s, ["COLOUR"])),
    fitting: clean(pick(s, ["FITTING"])),
    offer: clean(pick(s, ["SLEVES"])),
    qty: numStr(pick(s, ["Qty"], 0)),
    free: numStr(pick(s, ["FREE"], 0)),
    grossDp: numStr(pick(s, ["GrosDPrice"], 0)),
    plusPer: numStr(pick(s, ["DLRPLUSPER"], 0)),
    purDisPer: numStr(pick(s, ["DLRDISPER"], 0)),
    gstPer: numStr(pick(s, ["GST"], 0)),
    gstMode: "manual", // saved GST is kept until Gross DP is edited
    wPer: numStr(pick(s, ["WPER"], 0)),
    salePer: "",
    custPrice: numStr(pick(s, ["CPrice"], 0)),
    saleMode: "price",
    disPer: numStr(pick(s, ["Disper"], 0)),
  };

  const designCode = clean(pick(s, ["DesignCode"]));

  // maxnum / terminalid are stored on the stock row. If they are missing, they are
  // recovered from the design code  <PCode><FY><Terminal><seq>.
  let maxnum = Number(pick(s, ["MAXNUM"], NaN));
  if (!Number.isFinite(maxnum) || maxnum <= 0) {
    const fy = String(pick(s, ["FYEAR"], "")).slice(0, 2);
    const marker = `${fy}${base.prefix}`;
    const digits = designCode.replace(/^\D+/, "");
    maxnum =
      base.prefix && digits.startsWith(marker)
        ? num(digits.slice(marker.length))
        : 0;
  }
  let terminalid = Number(pick(s, ["TERMINALID"], NaN));
  if (!Number.isFinite(terminalid)) terminalid = Number(base.prefix) || 0;

  const calc = calcPurchaseRow(item);

  return {
    ...item,
    ...calc,
    // same rule as a new row; falls back to the stored CODE if the table is not available
    priceCode:
      buildPriceCode(calc.dirPrice, codeMap) || clean(pick(s, ["CODE"])),
    token: num(pick(s, ["TOKEN"], 0)),
    designCode,
    maxnum,
    terminalid,
  };
};

// position of the item inside its invoice: ECODE is "<entryno> / <sno>"
const stockSno = (s) => {
  const part = String(pick(s, ["ECODE"], "")).split("/")[1];
  const n = Number(String(part ?? "").trim());
  return Number.isFinite(n) ? n : num(pick(s, ["MAXNUM"], 0));
};

// TRANS_ENTRY_DATA can hold several rows for one entry no – the one carrying the amount wins
const pickTransRow = (rows) => {
  const F = FIELDS.transEntry;
  return rows.reduce(
    (best, r) =>
      !best || num(pick(r, F.amount, 0)) > num(pick(best, F.amount, 0))
        ? r
        : best,
    null,
  );
};

/* Loads ONE saved invoice. Called when a row is clicked in the Saved-invoices dialog.
   Two fresh API calls (GetDataFromGivenTableNameWithWhere, where ENTRYNO = entry no):

     1) TRANS_ENTRY_DATA → TOTALS (qty = JAMAQTY, net amount = JAMA)
                           + invoice no / date, supplier, city, purchase type, narration
     2) CURRENT_STOCK    → every ITEM row of the invoice

   A third call, PURCHINVOICE_MAST, only fills transport / LR / due days / trade discount /
   charges (not in the two tables above; Update would blank them otherwise). If it fails the
   invoice still opens and masterMissing = true so the screen can warn.

   Returns { header, footer, rows, supplier, entryDate, totals, masterMissing } */
export const loadSavedInvoice = async (
  tenantName,
  listRow,
  designBase,
  codeMap,
) => {
  const entryNo = Number(listRow.entryNo);
  const where = `ENTRYNO=${entryNo}`;
  const F = FIELDS.transEntry;

  const [transRes, stockRes, mastRes] = await Promise.allSettled([
    fetchTableWhere(TABLES.transEntry, where, tenantName),
    fetchTableWhere(TABLES.currentStock, where, tenantName),
    fetchTableWhere(TABLES.purchaseMaster, where, tenantName),
  ]);

  if (transRes.status === "rejected") throw transRes.reason;
  if (stockRes.status === "rejected") throw stockRes.reason;

  const t = pickTransRow(transRes.value);
  if (!t) throw new Error(`Entry ${entryNo} was not found in TRANS_ENTRY_DATA`);

  const lines = [...stockRes.value].sort((a, b) => stockSno(a) - stockSno(b));
  if (!lines.length) {
    throw new Error(
      `Item details for entry ${entryNo} were not found in CURRENT_STOCK`,
    );
  }

  if (mastRes.status === "rejected") {
    console.error("PURCHINVOICE_MAST not read", mastRes.reason);
  }
  const m = mastRes.status === "fulfilled" ? mastRes.value[0] || null : null;

  const base = designBase || { prefix: "", maxSeq: 0 };
  const rows = lines.map((s) => stockToRow(s, base, codeMap));

  const city = clean(pick(t, F.area)) || clean(pick(m, ["area"]));
  const name = clean(pick(t, F.party)) || clean(pick(m, ["partyname"]));
  const station = clean(pick(m, ["station"])).toUpperCase() || "LOCAL";

  // supplier row (for GSTIN / PAN / station / address); minimal fallback if not found
  let supplier = null;
  try {
    const list = await fetchSuppliersByArea(tenantName, city);
    supplier =
      list.find(
        (r) =>
          clean(pick(r, FIELDS.supplier.name)).toUpperCase() ===
          name.toUpperCase(),
      ) || null;
  } catch (e) {
    console.error("Supplier lookup failed", e);
  }
  if (!supplier) supplier = { Custname: name, area: city, Station: station };

  const transport = clean(pick(m, ["transport"]));
  const lrNo = clean(pick(m, ["lrno"]));
  const invoiceDate = dayjs(pick(t, F.billDate, undefined));

  const header = {
    purchaseType:
      clean(pick(t, F.transType)) ||
      clean(pick(t, F.particulars)) ||
      clean(pick(m, ["pType"])) ||
      null,
    city,
    supplier: name,
    invoiceNo: String(pick(t, F.billNo, "")).trim(),
    invoiceDate,
    stockRecDate: m ? dayjs(pick(m, ["srecdate"], undefined)) : invoiceDate,
    transport: transport || null,
    lrNo,
    // LR date is always saved by the payload, so only show it when LR / transport exist
    lrDate: transport || lrNo ? dayjs(pick(m, ["lrdate"], undefined)) : null,
    dueDays: String(num(pick(m, ["ddays"], 0))),
  };

  const footer = {
    otherAdditions: numStr(pick(m, ["oaddition"], 0)),
    tradeDisPer: numStr(pick(m, ["disrate"], 0)),
    adjustment: numStr(pick(m, ["adjAmt"], 0)),
    freight: numStr(pick(m, ["fcharges"], 0)),
    otherChrgs: numStr(pick(m, ["ocharges"], 0)),
    narration: clean(pick(t, ["NARRATION"])) || clean(pick(m, ["narration"])),
  };

  return {
    header,
    footer,
    rows,
    supplier,
    entryDate: pick(t, F.entryDate, null),
    // totals exactly as saved in TRANS_ENTRY_DATA
    totals: {
      totalQty: num(pick(t, F.qty, 0)),
      netAmount: num(pick(t, F.amount, 0)),
    },
    masterMissing: !m,
  };
};
